/**
 * Nährwerte: Richtwerte für häufige Zutaten, Umrechnung der Mengen in Gramm
 * und Summe für ein Rezept.
 *
 * Die Werte sind gerundete Durchschnittswerte je 100 g (roh bzw. wie
 * gekauft) und dienen der Orientierung, nicht der Ernährungsberatung. Eigene
 * Werte je Produkt haben Vorrang; Phase 5 ergänzt Werte aus freien
 * Datenbanken.
 */
import { densityFor } from './conversion.ts';
import { parseQuantity, scaleQuantity } from './quantity.ts';
import type { ScalingMode } from './types.ts';
import { normalizeUnit } from './units.ts';

/** Nährwerte je 100 g. */
export interface Nutrients {
  readonly kcal: number;
  readonly protein: number;
  readonly fat: number;
  readonly carbs: number;
  readonly sugar: number;
  readonly fiber: number;
  readonly salt: number;
}

/** Reihenfolge der Felder und ihre Einheit; die Bezeichnungen stehen in der Oberfläche. */
export const NUTRIENT_FIELDS: readonly { key: keyof Nutrients; unit: 'kcal' | 'g' }[] = [
  { key: 'kcal', unit: 'kcal' },
  { key: 'protein', unit: 'g' },
  { key: 'fat', unit: 'g' },
  { key: 'carbs', unit: 'g' },
  { key: 'sugar', unit: 'g' },
  { key: 'fiber', unit: 'g' },
  { key: 'salt', unit: 'g' },
];

/** Leere Nährwerte. */
export const ZERO_NUTRIENTS: Nutrients = {
  kcal: 0,
  protein: 0,
  fat: 0,
  carbs: 0,
  sugar: 0,
  fiber: 0,
  salt: 0,
};

/** Kurzschreibweise für die Tabelle: kcal, Eiweiß, Fett, KH, Zucker, Ballaststoffe, Salz. */
function n(
  kcal: number,
  protein: number,
  fat: number,
  carbs: number,
  sugar = 0,
  fiber = 0,
  salt = 0,
): Nutrients {
  return { kcal, protein, fat, carbs, sugar, fiber, salt };
}

/**
 * Richtwerte je 100 g. Genauere Stichwörter stehen vorn („Eigelb“ vor „Ei“,
 * „Kokosmilch“ vor „Milch“). piece ist das Gewicht eines Stücks in Gramm.
 */
const GENERIC: readonly (readonly [RegExp, Nutrients, number?])[] = [
  [/eigelb/, n(320, 16, 28, 0.3), 18],
  [/eiweiß|eiweiss|eiklar/, n(48, 11, 0.2, 0.7), 33],
  [/(^|\s)ei(er)?(\s|$)/, n(137, 12, 9.5, 0.7, 0.7), 60],
  [/kokosmilch/, n(200, 2, 20, 3, 2)],
  [/buttermilch/, n(37, 3.3, 0.5, 4, 4, 0, 0.1)],
  [/erdnussbutter/, n(600, 25, 50, 15, 6, 6, 1)],
  [/butter/, n(740, 0.7, 82, 0.6, 0.6, 0, 0.03)],
  [/margarine/, n(720, 0, 80, 0.4)],
  [/olivenöl|rapsöl|sonnenblumenöl|öl\b/, n(884, 0, 100, 0)],
  [/schmand|saure sahne|crème fraîche|creme fraiche/, n(240, 2.5, 24, 3.4, 3.4)],
  [/schlagsahne|sahne/, n(290, 2.4, 30, 3.2, 3.2)],
  [/frischkäse/, n(250, 6, 23, 3, 3, 0, 0.8)],
  [/mascarpone/, n(430, 5, 44, 4, 4)],
  [/magerquark/, n(67, 12, 0.2, 4, 4)],
  [/quark/, n(110, 11, 5, 3.5, 3.5)],
  [/joghurt/, n(61, 3.5, 3.5, 4, 4, 0, 0.1)],
  [/parmesan|pecorino/, n(392, 36, 26, 0, 0, 0, 1.6)],
  [/mozzarella/, n(250, 18, 19, 1, 1, 0, 0.5)],
  [/feta|schafskäse/, n(265, 17, 21, 1, 1, 0, 2.5)],
  [/käse/, n(356, 25, 27, 0, 0, 0, 1.8)],
  [/milch/, n(64, 3.3, 3.5, 4.8, 4.8, 0, 0.1)],
  [/hackfleisch|hack\b/, n(260, 18, 21, 0, 0, 0, 0.2)],
  [/hähnchen|huhn|hühner|pute/, n(110, 23, 1.5, 0, 0, 0, 0.1)],
  [/speck|bacon/, n(400, 14, 38, 0, 0, 0, 3)],
  [/schinken/, n(130, 20, 5, 1, 1, 0, 3)],
  [/lachs/, n(200, 20, 13, 0)],
  [/thunfisch/, n(110, 26, 1, 0, 0, 0, 0.9)],
  [/garnele|shrimps/, n(90, 19, 1, 1)],
  [/fisch|kabeljau|seelachs/, n(80, 18, 0.7, 0)],
  [/schwein/, n(180, 20, 11, 0)],
  [/rind|gulasch|steak/, n(150, 21, 7, 0)],
  [/fleisch/, n(170, 20, 10, 0)],
  [/vollkornmehl/, n(320, 12, 2.4, 60, 0.5, 11)],
  [/paniermehl|semmelbrösel/, n(370, 11, 2, 74, 4, 4, 1.2)],
  [/speisestärke|stärke/, n(350, 0.3, 0.1, 86)],
  [/mehl/, n(340, 10, 1, 71, 0.7, 4)],
  [/haferflocken/, n(370, 13, 7, 59, 1, 10)],
  [/grieß/, n(330, 10, 1, 69, 0.5, 4)],
  [/(^|\s)reis/, n(350, 7, 0.6, 78, 0.2, 1.4)],
  [/nudel|spaghetti|penne|pasta|lasagne|tagliatelle|fusilli/, n(355, 12, 1.5, 72, 3, 3)],
  [/couscous|bulgur/, n(355, 12, 1.5, 72, 1, 5)],
  [/linsen/, n(330, 24, 1.5, 49, 2, 11)],
  [/kichererbsen/, n(120, 7, 2.5, 16, 0.5, 5, 0.6)],
  [/kidneybohnen|weiße bohnen/, n(100, 7, 0.5, 14, 1, 6, 0.6)],
  [/puderzucker|zucker/, n(400, 0, 0, 100, 100)],
  [/honig/, n(304, 0.3, 0, 82, 82)],
  [/sirup/, n(280, 0, 0, 70, 70)],
  [/marmelade|konfitüre/, n(250, 0.4, 0.1, 60, 58)],
  [/schokolade|kuvertüre/, n(540, 7, 32, 50, 45, 8)],
  [/kakao/, n(360, 20, 21, 11, 1, 30)],
  [/mandel/, n(600, 24, 54, 6, 4, 13)],
  [/haselnuss|haselnüsse/, n(640, 15, 61, 11, 4, 8)],
  [/walnuss|walnüsse/, n(670, 15, 63, 11, 3, 6)],
  [/nüsse|nuss\b/, n(630, 17, 58, 12, 4, 8)],
  [/rosinen/, n(300, 3, 0.5, 70, 60, 5)],
  [/backpulver/, n(100, 0, 0, 25, 0, 0, 20)],
  [/hefe/, n(100, 12, 1, 4)],
  [/gelatine/, n(340, 85, 0, 0)],
  [/tomatenmark/, n(80, 4.5, 0.5, 13, 11, 4, 0.2)],
  [/passiert|stückige tomaten|dosentomaten/, n(25, 1.3, 0.2, 4, 4, 1, 0.2)],
  [/tomate/, n(18, 1, 0.2, 2.6, 2.6, 1.2), 100],
  [/süßkartoffel/, n(86, 1.6, 0.1, 20, 4, 3), 300],
  [/kartoffel/, n(77, 2, 0.1, 17, 0.8, 2), 150],
  [/zwiebel|schalotte/, n(40, 1.2, 0.2, 8, 5, 1.8), 100],
  [/knoblauch/, n(140, 6, 0.1, 28, 1, 2), 4],
  [/möhre|karotte/, n(36, 0.9, 0.2, 7, 5, 3), 80],
  [/paprikapulver|pfeffer|curry|zimt|kümmel|gewürz|oregano|thymian getrocknet/, n(300, 12, 10, 40, 5, 25)],
  [/paprika/, n(30, 1, 0.3, 5, 4, 2), 150],
  [/gurke/, n(13, 0.6, 0.2, 2, 2, 0.5), 400],
  [/zucchini/, n(19, 1.6, 0.4, 2, 2, 1), 250],
  [/aubergine/, n(24, 1.2, 0.2, 2.5, 2, 3), 300],
  [/spinat/, n(23, 2.9, 0.4, 0.6, 0.4, 2.2)],
  [/brokkoli/, n(34, 3.5, 0.2, 2.7, 1.7, 3), 400],
  [/blumenkohl/, n(25, 2, 0.3, 2.5, 2, 2), 800],
  [/champignon|pilz/, n(22, 3, 0.3, 0.6, 0.3, 2)],
  [/lauch|porree|frühlingszwiebel/, n(30, 2, 0.3, 3, 2, 2), 200],
  [/sellerie/, n(18, 1, 0.2, 2, 2, 1.6)],
  [/kürbis/, n(26, 1, 0.1, 6, 3, 1)],
  [/erbsen/, n(80, 5, 0.4, 12, 4, 5)],
  [/bohnen/, n(32, 2, 0.2, 5, 1.5, 3)],
  [/mais/, n(80, 3, 1.2, 15, 4, 3, 0.5)],
  [/avocado/, n(160, 2, 15, 2, 0.5, 7), 150],
  [/apfel|äpfel/, n(54, 0.3, 0.2, 12, 10, 2), 150],
  [/banane/, n(93, 1.1, 0.2, 21, 17, 2), 120],
  [/zitrone|limette/, n(35, 0.7, 0.6, 3, 2.5, 0.3), 100],
  [/orange/, n(47, 1, 0.2, 9, 9, 2), 200],
  [/beere|erdbeer|himbeer|heidelbeer/, n(35, 0.8, 0.4, 6, 5, 3)],
  [/toast/, n(260, 8, 3.5, 48, 5, 3, 1.2)],
  [/brot|brötchen|baguette/, n(240, 8, 1.5, 47, 2, 4, 1.2)],
  [/blätterteig/, n(400, 6, 27, 35, 1, 1, 1)],
  [/senf/, n(90, 6, 4, 6, 4, 2, 3)],
  [/ketchup/, n(100, 1.5, 0.2, 23, 22, 0.5, 2)],
  [/sojasauce|sojasoße/, n(60, 8, 0, 5, 1, 0, 15)],
  [/essig/, n(20, 0, 0, 0.6)],
  [/brühe|fond/, n(5, 0.3, 0.2, 0.5, 0.2, 0, 1)],
  [/wein/, n(75, 0, 0, 2.5, 1)],
  [/salz/, n(0, 0, 0, 0, 0, 0, 100)],
  [
    /petersilie|schnittlauch|basilikum|dill|koriander|minze|kräuter|rosmarin|thymian/,
    n(45, 3.5, 0.6, 6, 1, 4),
  ],
  [/wasser/, n(0, 0, 0, 0)],
];

/** Richtwerte zu einem Zutatennamen oder null. */
export function genericNutrients(name: string): Nutrients | null {
  const lower = ` ${name.toLowerCase()} `;
  return GENERIC.find(([pattern]) => pattern.test(lower))?.[1] ?? null;
}

/** Gewicht eines Stücks laut Tabelle oder null. */
function pieceWeight(name: string): number | null {
  const lower = ` ${name.toLowerCase()} `;
  return GENERIC.find(([pattern]) => pattern.test(lower))?.[2] ?? null;
}

/** Übliche Gewichte von Packungen und Mengenangaben ohne Maß, in Gramm. */
function unitWeight(unit: string, name: string): number | null {
  const lower = name.toLowerCase();
  switch (unit.toLowerCase()) {
    case 'prise':
      return 0.5;
    case 'msp.':
    case 'messerspitze':
      return 0.5;
    case 'zehe':
    case 'zehen':
      return 4;
    case 'bund':
      return 30;
    case 'scheibe':
    case 'scheiben':
      return /toast/.test(lower) ? 25 : /käse|schinken/.test(lower) ? 20 : 50;
    case 'würfel':
      return /hefe/.test(lower) ? 42 : 10;
    case 'päckchen':
    case 'packung':
      return /backpulver/.test(lower)
        ? 15
        : /vanillezucker/.test(lower)
          ? 8
          : /hefe/.test(lower)
            ? 7
            : /gelatine/.test(lower)
              ? 9
              : null;
    case 'dose':
    case 'dosen':
      return 400;
    case 'becher':
      return /joghurt/.test(lower) ? 150 : 200;
    case 'glas':
      return 350;
    case 'blatt':
      return /gelatine/.test(lower) ? 1.7 : null;
    default:
      return null;
  }
}

/** Gramm je Gewichtseinheit und Milliliter je Volumeneinheit. */
const MASS: Readonly<Record<string, number>> = { mg: 0.001, g: 1, kg: 1000 };
const VOLUME: Readonly<Record<string, number>> = {
  ml: 1,
  cl: 10,
  dl: 100,
  l: 1000,
  TL: 5,
  EL: 15,
  Tasse: 250,
};

/**
 * Gewicht einer Menge in Gramm. Volumen wird über die Dichte umgerechnet
 * (ohne bekannte Dichte wie Wasser), Stücke über typische Gewichte.
 * @param pieceGrams Eigenes Stückgewicht, zum Beispiel aus einem Produkt.
 * @returns null, wenn sich kein Gewicht schätzen lässt.
 */
export function gramsOf(
  value: number,
  unit: string,
  name: string,
  pieceGrams: number | null = null,
): number | null {
  const normalized = normalizeUnit(unit);
  const mass = MASS[normalized.toLowerCase()];
  if (mass !== undefined) {
    return value * mass;
  }
  const volume = VOLUME[normalized] ?? VOLUME[normalized.toLowerCase()];
  if (volume !== undefined) {
    return value * volume * (densityFor(name) ?? 1);
  }
  if (normalized === '' || normalized === 'Stück') {
    const piece = pieceGrams ?? pieceWeight(name);
    return piece === null ? null : value * piece;
  }
  const weight = unitWeight(normalized, name);
  return weight === null ? null : value * weight;
}

/** Eigene Werte zu einer Zutat. */
export interface NutritionSource {
  /** Nährwerte je 100 g; null, wenn nur das Stückgewicht bekannt ist. */
  readonly values: Nutrients | null;
  /** Gewicht eines Stücks in Gramm, falls bekannt. */
  readonly pieceGrams: number | null;
}

/** Was von einer Zutat gebraucht wird. */
export interface NutritionIngredient {
  readonly amount: string;
  readonly unit: string;
  readonly name: string;
  readonly scaling: ScalingMode;
}

/** Ergebnis für ein Rezept. */
export interface RecipeNutrition {
  /** Summe für die gewählte Menge. */
  readonly total: Nutrients;
  /** Zutaten mit Menge, die berechnet werden konnten. */
  readonly counted: number;
  /** Zutaten mit Menge insgesamt (ohne „nach Geschmack“). */
  readonly measurable: number;
  /** Zutaten, für die Werte oder Gewicht fehlen. */
  readonly missing: readonly string[];
}

/**
 * Summiert die Nährwerte eines Rezepts.
 * @param lookup Liefert eigene Werte (zum Beispiel aus Produkten); sonst gelten die Richtwerte.
 */
export function recipeNutrition<T extends NutritionIngredient>(
  ingredients: readonly T[],
  factor: number,
  lookup: (ingredient: T) => NutritionSource | null = () => null,
): RecipeNutrition {
  const total: Record<keyof Nutrients, number> = { ...ZERO_NUTRIENTS };
  let counted = 0;
  let measurable = 0;
  const missing: string[] = [];
  for (const ingredient of ingredients) {
    const quantity = parseQuantity(ingredient.amount);
    if (quantity === null) {
      continue;
    }
    measurable += 1;
    const scaled = scaleQuantity(quantity, factor, ingredient.scaling);
    const own = lookup(ingredient);
    const grams = gramsOf(scaled.value, ingredient.unit, ingredient.name, own?.pieceGrams ?? null);
    const values = own?.values ?? genericNutrients(ingredient.name);
    if (grams === null || values === null) {
      missing.push(ingredient.name);
      continue;
    }
    counted += 1;
    for (const field of NUTRIENT_FIELDS) {
      total[field.key] += (values[field.key] * grams) / 100;
    }
  }
  return { total, counted, measurable, missing };
}

/** Teilt Nährwerte, zum Beispiel durch die Zahl der Portionen. */
export function divideNutrients(values: Nutrients, divisor: number): Nutrients {
  const safe = divisor > 0 ? divisor : 1;
  return Object.fromEntries(
    NUTRIENT_FIELDS.map((field) => [field.key, values[field.key] / safe]),
  ) as unknown as Nutrients;
}

/** Prüft eigene Nährwerte aus einer Eingabe; fehlende Felder werden 0. */
export function parseNutrients(value: unknown): Nutrients | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const record = value as Record<string, unknown>;
  const result: Record<keyof Nutrients, number> = { ...ZERO_NUTRIENTS };
  let any = false;
  for (const field of NUTRIENT_FIELDS) {
    const raw = record[field.key];
    const number =
      typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw.replace(',', '.')) : NaN;
    if (Number.isFinite(number) && number >= 0 && number <= (field.key === 'kcal' ? 1000 : 100)) {
      result[field.key] = number;
      any = true;
    }
  }
  return any ? result : null;
}
